import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, Eye, Edit, Copy, Trash2, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown, ChevronsUpDown,
  Globe, CheckCircle, ExternalLink, Lock
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const DNS = () => {
  const navigate = useNavigate();
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('assets', 'dns');
  
  // Stats
  const [stats, setStats] = useState({
    sources: { total: 0, external: 0, internal: 0, active: 0 }
  });
  
  // Sources state
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', source_type: '', is_active: '' });
  const [searchInput, setSearchInput] = useState(''); // Input inmediato para debounce
  const [sortConfig, setSortConfig] = useState({ field: 'name', dir: 'asc' });
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  
  // Modals
  const [createModal, setCreateModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingSource, setEditingSource] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, source: null });
  const [formData, setFormData] = useState({
    name: '', domain: '', source_type: 'external', dns_server: '',
    check_subdomains: true, check_email_security: true, auto_scan_ssl: false, is_active: true
  });
  const [saving, setSaving] = useState(false);
  
  const pageSize = 25;

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/dns/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSources = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.source_type) params.append('source_type', filters.source_type);
      if (filters.is_active !== '') params.append('is_active', filters.is_active);

      const response = await api.get(`/assets/dns/sources?${params}`);
      setSources(response.data.items);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar dominios');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchSources();
  }, [page, filters, sortConfig]);

  // Debounce para filtro de búsqueda (800ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters(prev => ({ ...prev, search: searchInput }));
        setPage(1);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleStatFilter = (filterType) => {
    setActiveStatFilter(filterType);
    setPage(1);
    setSearchInput('');
    if (filterType === 'all') {
      setFilters({ search: '', source_type: '', is_active: '' });
    } else if (filterType === 'external') {
      setFilters(prev => ({ ...prev, source_type: 'external', is_active: '' }));
    } else if (filterType === 'internal') {
      setFilters(prev => ({ ...prev, source_type: 'internal', is_active: '' }));
    } else if (filterType === 'active') {
      setFilters(prev => ({ ...prev, source_type: '', is_active: 'true' }));
    }
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleToggleActive = async (source) => {
    try {
      const response = await api.patch(`/assets/dns/sources/${source.id}/toggle-active`);
      toast.success(response.data.message);
      fetchSources();
      fetchStats();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleCreate = () => {
    setFormData({
      name: '', domain: '', source_type: 'external', dns_server: '',
      check_subdomains: true, check_email_security: true, auto_scan_ssl: false, is_active: true
    });
    setModalMode('create');
    setEditingSource(null);
    setCreateModal(true);
  };

  const openEditModal = (source) => {
    setFormData({
      name: source.name || '',
      domain: source.domain || '',
      source_type: source.source_type || 'external',
      dns_server: source.dns_server || '',
      check_subdomains: source.check_subdomains ?? true,
      check_email_security: source.check_email_security ?? true,
      auto_scan_ssl: source.auto_scan_ssl ?? false,
      is_active: source.is_active ?? true
    });
    setModalMode('edit');
    setEditingSource(source);
    setCreateModal(true);
  };

  const openCopyModal = (source) => {
    setFormData({
      name: `${source.name} (copia)`,
      domain: '',
      source_type: source.source_type || 'external',
      dns_server: source.dns_server || '',
      check_subdomains: source.check_subdomains ?? true,
      check_email_security: source.check_email_security ?? true,
      auto_scan_ssl: source.auto_scan_ssl ?? false,
      is_active: true
    });
    setModalMode('create');
    setEditingSource(null);
    setCreateModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!formData.domain.trim()) {
      toast.error('El dominio es requerido');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        domain: formData.domain.trim(),
        source_type: formData.source_type,
        dns_server: formData.dns_server || null,
        check_subdomains: formData.check_subdomains,
        check_email_security: formData.check_email_security,
        auto_scan_ssl: formData.auto_scan_ssl,
        is_active: formData.is_active
      };

      if (modalMode === 'edit' && editingSource) {
        await api.put(`/assets/dns/sources/${editingSource.id}`, payload);
        toast.success('Dominio actualizado exitosamente');
      } else {
        await api.post('/assets/dns/sources', payload);
        toast.success('Dominio creado exitosamente');
      }
      setCreateModal(false);
      fetchSources();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/dns/sources/${deleteModal.source.id}`);
      toast.success('Dominio y registros eliminados');
      setDeleteModal({ open: false, source: null });
      fetchSources();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  if (!canRead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tienes permisos para ver DNS</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">DNS</h1>
            <p className="page-subtitle">Gestion de Dominios y Zonas DNS</p>
          </div>
          {canCreate && (
            <Button onClick={handleCreate} className="btn btn-primary" data-testid="new-dns-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Dominio
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="dns-stats">
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeStatFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleStatFilter('all')}
            data-testid="stats-total"
          >
            <Globe className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.sources.total}</div>
              <div className="stats-card-label">Total</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-blue-500/50 ${activeStatFilter === 'external' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => handleStatFilter('external')}
            data-testid="stats-external"
          >
            <ExternalLink className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-blue-500">{stats.sources.external}</div>
              <div className="stats-card-label">Externos</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-purple-500/50 ${activeStatFilter === 'internal' ? 'ring-2 ring-purple-500' : ''}`}
            onClick={() => handleStatFilter('internal')}
            data-testid="stats-internal"
          >
            <Lock className="h-6 w-6 text-purple-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-purple-500">{stats.sources.internal}</div>
              <div className="stats-card-label">Internos</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-green-500/50 ${activeStatFilter === 'active' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => handleStatFilter('active')}
            data-testid="stats-active"
          >
            <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.sources.active}</div>
              <div className="stats-card-label">Activos</div>
            </div>
          </div>
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('name')}
                        >
                          <div className="flex items-center gap-1">
                            NOMBRE
                            <SortIcon field="name" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('domain')}
                        >
                          <div className="flex items-center gap-1">
                            DOMINIO
                            <SortIcon field="domain" />
                          </div>
                        </th>
                        <th>TIPO</th>
                        <th className="text-center">ACTIVO</th>
                        <th className="text-center">SUBDOMINIOS</th>
                        <th className="text-center">EMAIL SEC.</th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none text-right"
                          onClick={() => handleSort('last_scan_records')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            RECORDS
                            <SortIcon field="last_scan_records" />
                          </div>
                        </th>
                        <th className="w-24 text-center">ACCIONES</th>
                      </tr>
                      {/* Filters */}
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Buscar..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-search"
                          />
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <select
                            value={filters.source_type}
                            onChange={(e) => handleFilterChange('source_type', e.target.value)}
                            className="h-7 w-full text-xs bg-background border border-border rounded px-2"
                          >
                            <option value="">Todos</option>
                            <option value="external">External</option>
                            <option value="internal">Internal</option>
                          </select>
                        </th>
                        <th className="py-2 px-4">
                          <select
                            value={filters.is_active}
                            onChange={(e) => handleFilterChange('is_active', e.target.value)}
                            className="h-7 w-full text-xs bg-background border border-border rounded px-2"
                          >
                            <option value="">Todos</option>
                            <option value="true">Si</option>
                            <option value="false">No</option>
                          </select>
                        </th>
                        <th colSpan={4}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sources.length === 0 ? (
                        <tr>
                          <td colSpan={8}>
                            <div className="empty-state">
                              <Globe className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron dominios</p>
                            </div>
                          </td>
                        </tr>
                      ) : sources.map((source) => (
                        <tr key={source.id} data-testid={`dns-row-${source.id}`}>
                          <td 
                            className="font-medium text-primary cursor-pointer hover:underline"
                            onClick={() => navigate(`/assets/dns/${source.id}`)}
                          >
                            {source.name}
                          </td>
                          <td className="font-mono text-sm">{source.domain}</td>
                          <td>
                            <span className={`badge ${source.source_type === 'external' ? 'badge-blue' : 'badge-purple'}`}>
                              {source.source_type}
                            </span>
                          </td>
                          <td className="text-center">
                            <Switch
                              checked={source.is_active}
                              onCheckedChange={() => canUpdate && handleToggleActive(source)}
                              disabled={!canUpdate}
                              data-testid={`toggle-active-${source.id}`}
                            />
                          </td>
                          <td className="text-center">
                            {source.check_subdomains ? (
                              <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="text-center">
                            {source.check_email_security ? (
                              <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="text-right font-mono text-sm">{source.last_scan_records || 0}</td>
                          <td className="w-24">
                            <div className="flex justify-center gap-0.5">
                              <button
                                onClick={() => navigate(`/assets/dns/${source.id}`)}
                                className="action-btn"
                                title="Ver detalle"
                                data-testid={`view-dns-${source.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              {canUpdate && (
                                <button
                                  onClick={() => openEditModal(source)}
                                  className="action-btn"
                                  title="Editar"
                                  data-testid={`edit-dns-${source.id}`}
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              )}
                              {canCreate && (
                                <button
                                  onClick={() => openCopyModal(source)}
                                  className="action-btn"
                                  title="Copiar"
                                  data-testid={`copy-dns-${source.id}`}
                                >
                                  <Copy className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                onClick={() => canDelete && setDeleteModal({ open: true, source })}
                                className={`action-btn action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                                disabled={!canDelete}
                                title="Eliminar"
                                data-testid={`delete-dns-${source.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <div className="text-sm text-muted-foreground">
                    {total > 0 ? `${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} de ${total}` : '0 registros'}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">{page} / {totalPages || 1}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages || totalPages === 0}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Create Modal */}
        <Dialog open={createModal} onOpenChange={setCreateModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                {modalMode === 'edit' ? 'Editar Dominio' : 'Nuevo Dominio'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Nombre *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Mi Dominio"
                    data-testid="input-name"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Dominio *</Label>
                  <Input
                    value={formData.domain}
                    onChange={(e) => setFormData(prev => ({ ...prev, domain: e.target.value }))}
                    placeholder="example.com"
                    data-testid="input-domain"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Tipo</Label>
                  <select
                    value={formData.source_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, source_type: e.target.value }))}
                    className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                    data-testid="select-type"
                  >
                    <option value="external">External</option>
                    <option value="internal">Internal</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">DNS Server (opcional)</Label>
                  <Input
                    value={formData.dns_server}
                    onChange={(e) => setFormData(prev => ({ ...prev, dns_server: e.target.value }))}
                    placeholder="8.8.8.8"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <Label className="text-sm">Activo</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                    data-testid="switch-active"
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <Label className="text-sm">Check Subdominios</Label>
                  <Switch
                    checked={formData.check_subdomains}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, check_subdomains: checked }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <Label className="text-sm">Check Email Security</Label>
                  <Switch
                    checked={formData.check_email_security}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, check_email_security: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <Label className="text-sm">Auto Scan SSL</Label>
                  <Switch
                    checked={formData.auto_scan_ssl}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, auto_scan_ssl: checked }))}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, source: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar Dominio
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              Estas seguro que deseas eliminar el dominio <strong>{deleteModal.source?.name}</strong>?
              <br />
              <span className="text-amber-500 text-sm">Esto eliminara tambien todos los registros DNS y datos de email security asociados.</span>
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, source: null })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DNS;
